import type { MutableRefObject } from 'react';
import { useEffect } from 'react';

/**
 * Calls a handler when a pointer event occurs outside of the given ref
 *
 * @param ref clicks outside of this ref will invoke handler
 * @param handler passed the event that triggered the call
 */
export const useOnClickOutside = <T extends Node | null>(
  ref: MutableRefObject<T>,
  handler: (e: PointerEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || !(event.target instanceof Node) || ref.current.contains(event.target)) {
        return;
      }

      handler(event);
    };
    document.addEventListener('pointerdown', listener);

    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [ref, handler]);
};
